/**
 * Quick-action button-set resolution for thread-history responses.
 *
 * `list-messages` and `get-shared-thread-messages` send each distinct set of
 * quick-action buttons once, in a top-level `quick_action_sets` map, and leave
 * only `payload.buttons_ref` on every `quick_actions` message. Resolving the
 * refs where the response arrives (the fetch thunks) means reducers, selectors
 * and components keep seeing inline `payload.buttons` — the same shape the old
 * response and every WebSocket frame deliver.
 *
 * Each resolved message gets its own deep copy of the set: Immer assumes no
 * object appears twice in the state tree, and the old response gave every turn
 * distinct objects. A response without `quick_action_sets` (the pre-compaction
 * shape) is returned as-is, by reference.
 */
import { MESSAGE_TYPES } from "src/domain/messages";

export interface QuickActionSetsResponse {
  messages?: unknown;
  quick_action_sets?: Record<string, unknown> | null;
  [key: string]: unknown;
}

interface RefMessage {
  message_type?: unknown;
  payload?: { buttons_ref?: unknown; [key: string]: unknown } | null;
  [key: string]: unknown;
}

const hasOwn = (obj: object, key: string): boolean => Object.prototype.hasOwnProperty.call(obj, key);

export const resolveQuickActionRefs = <T extends QuickActionSetsResponse | null | undefined>(data: T): T => {
  const sets = data?.quick_action_sets;
  const messages = data?.messages;
  if (!sets || typeof sets !== "object" || !Array.isArray(messages)) return data;

  const serialised = new Map<string, string>();
  const buttonsFor = (ref: string): unknown[] => {
    const set = hasOwn(sets, ref) ? sets[ref] : undefined;
    if (!Array.isArray(set)) return [];
    let json = serialised.get(ref);
    if (json === undefined) {
      json = JSON.stringify(set);
      serialised.set(ref, json);
    }
    return JSON.parse(json);
  };

  return {
    ...data,
    messages: messages.map((message: RefMessage | null | undefined) => {
      const payload = message?.payload;
      const ref = message?.message_type === MESSAGE_TYPES.QUICK_ACTIONS ? payload?.buttons_ref : undefined;
      if (ref == null || !payload) return message;
      const { buttons_ref, ...rest } = payload;
      return { ...message, payload: { ...rest, buttons: buttonsFor(String(buttons_ref)) } };
    }),
  } as T;
};
